import { makeAutoObservable, runInAction } from "mobx";
import agent from "../../api/agent";
import { ReportInput } from "../../models/reports";
import { PagingParams, Pagination } from "../../models/pagination";
import { DateRangeOption, ReportType } from "../../models/enums";
import { OrderDirection } from "../../models/ticket";
import { AccountsReceivableAgingReportItemDto } from "../../models/accountsReceivable";

export default class AccountsReceivableAgingReportStore {
    constructor() {
        makeAutoObservable(this);
    }

    loadingInitial = false;
    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    };

    reportCriteria: ReportInput = new ReportInput({
        dateRange: DateRangeOption.AllTime,
        startDate: new Date(0),
        endDate: new Date(),
        reportType: ReportType.Summary,
        filter: "0"
    });

    accountsReceivableAgingReportRegistry = new Map<string, AccountsReceivableAgingReportItemDto>();

    resetARReportRegistry = () => {
        this.accountsReceivableAgingReportRegistry.clear();
    };

    loadAccountsReceivableAgingReport = async () => {
        this.loadingInitial = true;
        try {
            this.accountsReceivableAgingReportRegistry.clear();
            const result = await agent.accountsReceivableReports.getAccountsReceivableAgingReport(this.axiosParams);
            runInAction(() => {
                result.forEach((accountsReceivableAgingReport) => {
                    this.setAccountsReceivableAgingReport(accountsReceivableAgingReport);
                });
            });
        } catch (error) {
            console.log(error);
        } finally {
            this.setLoadingInitial(false);
        }
    };

    private setAccountsReceivableAgingReport = (accountsReceivableAgingReport: AccountsReceivableAgingReportItemDto) => {
        this.accountsReceivableAgingReportRegistry.set(accountsReceivableAgingReport.ticketId ?? "", accountsReceivableAgingReport);
    };

    get getAccountsReceivableAgingReport() {
        return Array.from(this.accountsReceivableAgingReportRegistry.values()).sort(
            (a, b) => (a.companyName ?? a.customerName)!.localeCompare(b.companyName ?? b.customerName ?? "")
        )
    }

    get groupedAccountsReceivableAgingReport() {
        return Object.entries(
            this.getAccountsReceivableAgingReport.reduce((groups, ac) => {

                const name = `${ac.companyName
                                ? ac.companyName
                                : ac.customerName 
                                    ? ac.customerName
                                    : "No Customer Name Assigned"
                    }`;

                groups[name] = groups[name] ? [...groups[name], ac] : [ac];
                return groups;
            }, {} as { [key: string]: AccountsReceivableAgingReportItemDto[] })
        );
    }

    pagingParams = new PagingParams();
    pagination: Pagination | null = null;
    predicate = new Map().set("all", true);

    get axiosParams() {
        const params = new URLSearchParams();
        this.predicate.forEach((value, key) => {
            if (key === "startDate" || key === "endDate") {
                params.append(key, (value as Date).toISOString());
            } else {
                params.append(key, value);
            }
        });
        return params;
    }

    setPredicate = (
        predicateKey: string,
        value: string | Date | number | OrderDirection | undefined,
    ) => {
        if (value)
            this.predicate.set(predicateKey, value);
        else
            this.predicate.delete(predicateKey);
    };

    setReportCriteria = (values: ReportInput) => {
        this.reportCriteria = new ReportInput(values);
    };

    calculateTotalPayments = (data: AccountsReceivableAgingReportItemDto[]) => {
        const totalRefunded = data.reduce(
            (accumulator: number, item: AccountsReceivableAgingReportItemDto) => {
                accumulator += (item.paymentsReceived ?? 0);
                return accumulator;
            }, 0);

        return totalRefunded;
    };

    calculateOneToThirtyBalanceDue = (data: AccountsReceivableAgingReportItemDto[]) => {
        const totalRefunded = data.reduce(
            (accumulator: number, item: AccountsReceivableAgingReportItemDto) => {
                accumulator += (item.oneToThirtyPastBalanceDue ?? 0);
                return accumulator;
            }, 0);

        return totalRefunded;
    };
    calculateThirtyToSixtyBalanceDue = (data: AccountsReceivableAgingReportItemDto[]) => {
        const totalRefunded = data.reduce(
            (accumulator: number, item: AccountsReceivableAgingReportItemDto) => {
                accumulator += (item.thirtyToSixtyPastBalanceDue ?? 0);
                return accumulator;
            }, 0);

        return totalRefunded;
    };
    calculateSixtyToNinetyBalanceDue = (data: AccountsReceivableAgingReportItemDto[]) => {
        const totalRefunded = data.reduce(
            (accumulator: number, item: AccountsReceivableAgingReportItemDto) => {
                accumulator += (item.sixtyToNinetyPastBalanceDue ?? 0);
                return accumulator;
            }, 0);

        return totalRefunded;
    };
    calculateNinetyPlusBalanceDue = (data: AccountsReceivableAgingReportItemDto[]) => {
        const totalRefunded = data.reduce(
            (accumulator: number, item: AccountsReceivableAgingReportItemDto) => {
                accumulator += (item.ninetyPlusPastBalanceDue ?? 0);
                return accumulator;
            }, 0);

        return totalRefunded;
    };

    calculateTotalBalanceDue = (data: AccountsReceivableAgingReportItemDto[]) => {
        const totalRefunded = data.reduce(
            (accumulator: number, item: AccountsReceivableAgingReportItemDto) => {
                accumulator += (item.balanceDue ?? 0);
                return accumulator;
            }, 0);

        return totalRefunded;
    };
    
}
